import { zodResolver } from '@hookform/resolvers/zod';
import { changePasswordInputSchema } from 'chery-contracts';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { useAuth } from '../../auth/AuthContext';
import { landingPathFor } from '../../auth/landing-path';
import { Button } from '../../components/ui/Button';
import { FormField } from '../../components/ui/FormField';
import { Input } from '../../components/ui/Input';
import { isBadRequestError, serverMessage } from '../../lib/form-helpers';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { AuthCard, FormAlert } from './AuthCard';

/**
 * Forced after an Administrator set the password (`mustChangePassword`) and reachable any time from the shell.
 * The form validates against the contract's own input schema, plus a confirmation field that never leaves the
 * browser. Success returns a fresh token pair (every other device is signed out) and lands the user.
 */
export const ChangePasswordPage = () => {
  const { t } = useI18nTranslation();
  const { user, changePassword } = useAuth();
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const isForced = user?.mustChangePassword ?? false;

  const formSchema = useMemo(
    () =>
      changePasswordInputSchema
        .extend({ confirmPassword: z.string() })
        .refine((values) => values.newPassword === values.confirmPassword, { path: ['confirmPassword'], message: t('changePassword.mismatch') }),
    [t],
  );

  type ChangePasswordForm = z.infer<typeof formSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordForm>({
    resolver: zodResolver(formSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const onSubmit = handleSubmit(async ({ currentPassword, newPassword }) => {
    setSubmitError(null);

    try {
      const updated = await changePassword({ currentPassword, newPassword });

      navigate(landingPathFor(updated.role), { replace: true });
    } catch (error) {
      setSubmitError(isBadRequestError(error) ? (serverMessage(error) ?? t('changePassword.failed')) : t('changePassword.failed'));
    }
  });

  return (
    <AuthCard
      title={t('changePassword.title')}
      subtitle={isForced ? t('changePassword.forcedSubtitle') : t('changePassword.subtitle')}
    >
      <form
        onSubmit={onSubmit}
        noValidate
        className="mt-6 space-y-4"
      >
        <FormField
          id="currentPassword"
          label={t('changePassword.currentPassword')}
          error={errors.currentPassword?.message}
        >
          <Input
            id="currentPassword"
            type="password"
            autoComplete="current-password"
            autoFocus
            hasError={Boolean(errors.currentPassword)}
            {...register('currentPassword')}
          />
        </FormField>

        <FormField
          id="newPassword"
          label={t('changePassword.newPassword')}
          error={errors.newPassword?.message}
          hint={t('changePassword.rules')}
        >
          <Input
            id="newPassword"
            type="password"
            autoComplete="new-password"
            hasError={Boolean(errors.newPassword)}
            {...register('newPassword')}
          />
        </FormField>

        <FormField
          id="confirmPassword"
          label={t('changePassword.confirmPassword')}
          error={errors.confirmPassword?.message}
        >
          <Input
            id="confirmPassword"
            type="password"
            autoComplete="new-password"
            hasError={Boolean(errors.confirmPassword)}
            {...register('confirmPassword')}
          />
        </FormField>

        <FormAlert message={submitError} />

        <Button
          type="submit"
          isLoading={isSubmitting}
          className="w-full"
        >
          {isSubmitting ? t('changePassword.submitting') : t('changePassword.submit')}
        </Button>

        {!isForced && user ? (
          <Link
            to={landingPathFor(user.role)}
            className="block text-center text-sm text-muted-foreground hover:text-foreground"
          >
            {t('changePassword.cancel')}
          </Link>
        ) : null}
      </form>
    </AuthCard>
  );
};
